/* eslint-disable max-len */
import React, { useCallback, useEffect, useRef, useState } from 'react'
import './AIAssistSidePanel.css'
import { useAppSelector } from '@store/hooks'
import { aiTextActions } from '@hooks/useAiTextAssist'
import type { AssistantAction } from '@services/api/aiAssistant.api'
import { useAssistantConversation } from './useAssistantConversation'

import { createPortal } from 'react-dom'
import Button from '@components/common/Button/Button.component'
import { CHAT_LEFT_BUTTONS } from '@constants/Message'
import EmojiPicker, { EmojiClickData } from 'emoji-picker-react'
import { TooltipComponent } from '@syncfusion/ej2-react-popups'
import InsertLinkModal from '@components/common/InsertLinkModal/InsertLinkModal.component'
import { AssistantAnswer } from './AssistantAnswer'
import { AssistantAppointmentList } from './AssistantAppointmentList'
const VariableList = [
  '(clinic_name)',
  '(practice_services)',
  '(practice_number)',
].map((text) => ({ text, value: 'Fill in before sending' }))
const quickActions = [
  'Create summary',
  'Patient recommendation',
  'Suggest diagnosis',
  'Check coverage',
  'Create note',
  'Check treatment plan',
  'Search web',
]
type Props = { isHidden: boolean; toggle: () => void }
type Recognition = {
  continuous: boolean
  interimResults: boolean
  lang: string
  onresult:
    | ((event: {
        results: {
          [index: number]: { [index: number]: { transcript: string } }
        }
      }) => void)
    | null
  onerror: ((event: { error: string }) => void) | null
  onend: (() => void) | null
  start: () => void
  stop: () => void
  abort: () => void
}

const AssistantPanel: React.FC<{ clinicId: string; toggle: () => void }> = ({
  clinicId,
  toggle,
}) => {
  const chat = useAssistantConversation(clinicId)
  const { messages, sessions: chatSessions, sessionId: currentSessionId } = chat
  const [showEmojiPicker, setShowEmojiPicker] = useState(false)
  const [showVariableDropdown, setShowVariableDropdown] = useState(false)
  const [showInsertLinkModal, setShowInsertLinkModal] = useState(false)
  const [showWritingActions, setShowWritingActions] = useState(false)
  const [historyDropdownPosition, setHistoryDropdownPosition] = useState({
    top: 0,
    left: 0,
  })
  const historyDropdownRef = useRef<HTMLDivElement>(null)
  const toolbarRef = useRef<HTMLDivElement>(null)
  const [inputValue, setInputValue] = useState('')
  const [action, setAction] = useState<AssistantAction>('chat')
  const [editingMessageId, setEditingMessageId] = useState<string>()
  const [showHistoryDropdown, setShowHistoryDropdown] = useState(false)
  const [isRecording, setIsRecording] = useState<boolean>(false)
  const inputRef = useRef<HTMLInputElement>(null)
  const messagesEndRef = useRef<HTMLDivElement>(null)
  const recognitionRef = useRef<Recognition | null>(null)

  useEffect(
    () => () => {
      const recognition = recognitionRef.current
      if (recognition) {
        recognition.onresult = null
        recognition.onend = null
        recognition.onerror = null
        recognition.abort()
      }
    },
    [],
  )

  useEffect(() => {
    messagesEndRef.current?.scrollIntoView?.({ behavior: 'smooth' })
  }, [chat.messages])

  const resetComposer = (): void => {
    setInputValue('')
    setEditingMessageId(undefined)
    setAction('chat')
    setShowHistoryDropdown(false)
    setShowEmojiPicker(false)
    setShowVariableDropdown(false)
    setShowWritingActions(false)
  }

  const send = (): void => {
    if (!clinicId || !inputValue.trim() || chat.isLoading || isRecording) return
    const text = inputValue.trim()
    void chat.send(text, action, editingMessageId)
    setInputValue('')
    setEditingMessageId(undefined)
    setAction('chat')
  }

  const insertText = (text: string): void => {
    const position = inputRef.current?.selectionStart ?? inputValue.length
    setInputValue(
      (inputValue.slice(0, position) + text + inputValue.slice(position)).slice(
        0,
        3000,
      ),
    )
    inputRef.current?.focus()
  }
  const handleNewSession = (): void => {
    resetComposer()
    chat.newSession()
  }
  const handleSessionSelect = (id: string): void => {
    resetComposer()
    chat.selectSession(id)
  }
  const handleDeleteSession = (event: React.MouseEvent, id: string): void => {
    event.stopPropagation()
    if (id === currentSessionId) resetComposer()
    chat.deleteSession(id)
    if (chatSessions.length === 1) setShowHistoryDropdown(false)
  }
  const handleEditMessage = (id: string, content: string): void => {
    if (chat.isLoading) return
    setEditingMessageId(id)
    setInputValue(content)
    setAction(messages.find((message) => message.id === id)?.action ?? 'chat')
    inputRef.current?.focus()
  }
  const selectAction = (key: AssistantAction): void => {
    setAction(key)
    setShowWritingActions(false)
    inputRef.current?.focus()
  }
  const handleQuickActionClick = (label: string): void => {
    if (chat.isLoading) return
    if (label === 'Search web') {
      selectAction('web_search')
      return
    }
    setAction('chat')
    setEditingMessageId(undefined)
    setInputValue(`${label}: `)
    inputRef.current?.focus()
  }
  const handleToolbarAction = (key: string): void => {
    setShowEmojiPicker(key === 'emoji' && !showEmojiPicker)
    setShowVariableDropdown(key === 'variable' && !showVariableDropdown)
    setShowWritingActions(key === 'ai' && !showWritingActions)
    if (key === 'url') setShowInsertLinkModal(true)
  }
  const handleInsertLink = (url: string, label: string): void => {
    const href = /^https?:\/\//i.test(url) ? url : `https://${url}`
    insertText(`[${label}](${href})`)
  }
  const handleEmojiClick = (emoji: EmojiClickData): void => {
    insertText(emoji.emoji)
    setShowEmojiPicker(false)
  }
  const handleVariableSelect = (variable: { text: string }): void => {
    insertText(variable.text)
    setShowVariableDropdown(false)
  }
  const handleKeyPress = (
    event: React.KeyboardEvent<HTMLInputElement>,
  ): void => {
    if (event.key === 'Enter' && !event.nativeEvent.isComposing) {
      event.preventDefault()
      send()
    }
    if (event.key === 'Escape') {
      setEditingMessageId(undefined)
      setInputValue('')
      setAction('chat')
    }
  }
  useEffect(() => {
    const outside = (event: MouseEvent): void => {
      const target = event.target as HTMLElement
      if (
        !historyDropdownRef.current?.contains(target) &&
        !target.closest('.ai-assist-history-dropdown')
      )
        setShowHistoryDropdown(false)
      if (!toolbarRef.current?.contains(target)) {
        setShowEmojiPicker(false)
        setShowVariableDropdown(false)
        setShowWritingActions(false)
      }
    }
    document.addEventListener('mousedown', outside)
    return () => document.removeEventListener('mousedown', outside)
  }, [])
  const getIconForButton = (button: (typeof CHAT_LEFT_BUTTONS)[0]): string => {
    if (button.type === 'builtin') {
      switch (button.key) {
        case 'emoji':
          return 'ti ti-mood-smile'
        case 'url':
          return 'ti ti-link'
        default:
          return button.icon
      }
    }
    if (button.key === 'variable') {
      return 'ti ti-variable'
    }
    if (button.key === 'mention') {
      return 'mgc_at_line'
    }
    return button.icon
  }

  const updateHistoryDropdownPosition = useCallback((): void => {
    if (!historyDropdownRef.current) return

    const rect = historyDropdownRef.current.getBoundingClientRect()
    setHistoryDropdownPosition({
      top: rect.bottom + 8,
      left: rect.left,
    })
  }, [])

  useEffect(() => {
    if (!showHistoryDropdown) return

    updateHistoryDropdownPosition()

    window.addEventListener('resize', updateHistoryDropdownPosition)
    window.addEventListener('scroll', updateHistoryDropdownPosition, true)

    return () => {
      window.removeEventListener('resize', updateHistoryDropdownPosition)
      window.removeEventListener('scroll', updateHistoryDropdownPosition, true)
    }
  }, [showHistoryDropdown, updateHistoryDropdownPosition])

  const handleHistoryIconClick = (): void => {
    if (chatSessions.length > 0) {
      if (!showHistoryDropdown) {
        updateHistoryDropdownPosition()
      }
      setShowHistoryDropdown(!showHistoryDropdown)
    }
  }

  const formatTimestamp = (date: Date): string => {
    const now = new Date()
    const diffMs = now.getTime() - date.getTime()
    const diffMins = Math.floor(diffMs / 60000)
    const diffHours = Math.floor(diffMs / 3600000)
    const diffDays = Math.floor(diffMs / 86400000)

    if (diffMins < 60) {
      return `${diffMins} min ago`
    } else if (diffHours < 24) {
      return `${diffHours} hour${diffHours > 1 ? 's' : ''} ago`
    } else if (diffDays < 7) {
      return `${diffDays} day${diffDays > 1 ? 's' : ''} ago`
    } else {
      return date.toLocaleDateString()
    }
  }

  const handleMicrophoneClick = (): void => {
    setIsRecording(!isRecording)
  }

  return (
    <div
      className="h-screen flex flex-col bg-white ai-assist-sidepanel open"
      style={{
        width: '311px',
        border: '1px solid var(--blue-200)',
      }}
    >
      <div className="ai-assist-head">
        <div className="ai-assist-head-inner">
          <div className="ai-assist-head-left">
            <div className="ai-assist-logo">
              <div className="ai-assist-logo-icon-wrapper">
                <i className="mgc_dental_fill"></i>
                <svg
                  className="ai-assist-logo-overlay"
                  width="6"
                  height="6"
                  viewBox="0 0 6 6"
                  fill="none"
                  xmlns="http://www.w3.org/2000/svg"
                >
                  <path
                    d="M2.11357 1.0393C2.29381 0.511815 3.02261 0.49584 3.23631 0.991378L3.2544 1.03961L3.49763 1.75096C3.55337 1.9141 3.64345 2.06339 3.76179 2.18875C3.88013 2.31412 4.02398 2.41265 4.18363 2.47769L4.24904 2.50211L4.96035 2.74505C5.48782 2.9253 5.50379 3.65414 5.00858 3.86785L4.96035 3.88594L4.24904 4.12918C4.08585 4.18489 3.93651 4.27496 3.8111 4.39331C3.68569 4.51166 3.58712 4.65553 3.52204 4.81522L3.49763 4.88033L3.2547 5.59199C3.07446 6.11948 2.34566 6.13545 2.13226 5.64021L2.11357 5.59199L1.87064 4.88063C1.81493 4.71743 1.72487 4.56809 1.60653 4.44267C1.48819 4.31725 1.34432 4.21868 1.18464 4.1536L1.11954 4.12918L0.408218 3.88624C-0.119544 3.70599 -0.135519 2.97715 0.359993 2.76374L0.408218 2.74505L1.11954 2.50211C1.28267 2.44636 1.43195 2.35628 1.55731 2.23793C1.68266 2.11959 1.78119 1.97573 1.84623 1.81607L1.87064 1.75096L2.11357 1.0393ZM5.09539 5.44632e-08C5.15177 -7.11272e-08 5.20703 0.0158181 5.25487 0.0456569C5.30272 0.0754957 5.34124 0.118158 5.36605 0.168797L5.38052 0.204063L5.48601 0.513322L5.79555 0.61882C5.85206 0.638019 5.9016 0.673563 5.93788 0.720946C5.97417 0.76833 5.99558 0.82542 5.99939 0.884982C6.00319 0.944544 5.98924 1.0039 5.95928 1.05552C5.92932 1.10714 5.88472 1.1487 5.83112 1.17494L5.79555 1.18941L5.48631 1.29491L5.38082 1.60447C5.36158 1.66097 5.32602 1.71048 5.27862 1.74674C5.23122 1.783 5.17412 1.80437 5.11457 1.80815C5.05501 1.81193 4.99567 1.79794 4.94407 1.76796C4.89247 1.73797 4.85094 1.69335 4.82472 1.63974L4.81026 1.60447L4.70476 1.29521L4.39522 1.18971C4.33871 1.17051 4.28917 1.13497 4.25289 1.08759C4.2166 1.0402 4.19519 0.983114 4.19138 0.923552C4.18758 0.86399 4.20153 0.804638 4.23149 0.753017C4.26145 0.701397 4.30605 0.659832 4.35965 0.63359L4.39522 0.619121L4.70446 0.513624L4.80995 0.204063C4.83028 0.14451 4.86873 0.0928105 4.91992 0.0562149C4.9711 0.0196193 5.03246 -3.77626e-05 5.09539 5.44632e-08Z"
                    fill="#3A3AE9"
                    stroke="white"
                    strokeWidth="2"
                  />
                </svg>
              </div>
              <span>ENAMELAI</span>
            </div>
            <div className="ai-assist-actions">
              <Button
                className="button-new-session"
                aria-label="New chat"
                size="sm"
                onClick={handleNewSession}
              >
                <i className="mgc mgc_pencil_2_ai_fill"></i>
              </Button>
              <div
                className="ai-assist-history-wrapper"
                ref={historyDropdownRef}
              >
                <button
                  type="button"
                  aria-label="History"
                  disabled={!chatSessions.length}
                  aria-expanded={showHistoryDropdown}
                  className={`ai-assist-history-icon ${chatSessions.length === 0 ? 'inactive' : ''}`}
                  onClick={handleHistoryIconClick}
                >
                  <i className="mgc_history_anticlockwise_line"></i>
                </button>
              </div>
            </div>
          </div>
          <div className="ai-assist-collapse">
            <button
              type="button"
              className="unstyled-button collapse-icon ti ti-layout-sidebar-right-collapse"
              aria-label="Close AI assistant"
              onClick={toggle}
            />
          </div>
        </div>
      </div>

      <div
        className="ai-assist-body"
        role="log"
        aria-label="Conversation"
        aria-live="polite"
      >
        {messages.map((message, messageIndex) => (
          <React.Fragment key={message.id}>
            {message.type === 'user' ? (
              <div className="ai-assist-sender">
                <div className="ai-assist-message-container">
                  <button
                    type="button"
                    className="unstyled-button ai-assist-edit-icon"
                    onClick={() =>
                      handleEditMessage(message.id, message.content)
                    }
                    title="Edit message"
                    aria-label="Edit message"
                  >
                    <i className="mgc_pencil_fill" aria-hidden />
                  </button>
                  <div className="ai-assist-message">
                    <div className="ai-assist-message-content">
                      {message.content}
                    </div>
                  </div>
                </div>
              </div>
            ) : (
              <div className="ai-assist-response">
                <div className="ai-assist-response-container">
                  <div className="ai-assist-response-inner">
                    <div className="ai-assist-response-bubble">
                      <div className="ai-assist-response-content">
                        <div className="ai-assist-response-text">
                          {message.isTyping ? (
                            <span className="ai-assist-loading-shimmer">
                              {message.content}
                            </span>
                          ) : message.schedule ? (
                            <div style={{ whiteSpace: 'pre-line' }}>
                              <AssistantAnswer
                                content={message.content}
                                citations={message.citations}
                              />
                              <AssistantAppointmentList
                                schedule={message.schedule}
                              />
                            </div>
                          ) : message.scheduleList?.length ? (
                            <div className="ai-assist-schedule-list">
                              {message.scheduleList.map((item, index) => (
                                <div
                                  key={index}
                                  className="ai-assist-schedule-item"
                                >
                                  {item}
                                </div>
                              ))}
                            </div>
                          ) : (
                            <div style={{ whiteSpace: 'pre-line' }}>
                              <AssistantAnswer
                                content={message.content}
                                citations={message.citations}
                              />
                            </div>
                          )}
                        </div>
                        {message.failed &&
                          messageIndex === messages.length - 1 &&
                          !chat.isLoading && (
                            <button
                              type="button"
                              className="ai-assist-toolbar-button"
                              aria-label="Retry"
                              onClick={chat.retry}
                            >
                              <i className="ti ti-refresh" />
                            </button>
                          )}
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            )}
          </React.Fragment>
        ))}
        <div ref={messagesEndRef} />
      </div>

      <div className="ai-assist-footer">
        <div className="ai-assist-footer-title">How can I help?</div>
        <div className="ai-assist-badge-wrapper">
          {quickActions.map((label, index) => (
            <button
              type="button"
              disabled={chat.isLoading}
              key={index}
              className="ai-assist-badge"
              onClick={() => handleQuickActionClick(label)}
              style={{ cursor: 'pointer' }}
            >
              {label}
            </button>
          ))}
        </div>
        <div
          className="ai-assist-input-wrapper"
          style={{ position: 'relative' }}
          ref={toolbarRef}
        >
          <div className="ai-assist-toolbar">
            {CHAT_LEFT_BUTTONS.map((button) => (
              <TooltipComponent
                key={button.key}
                content={button.text}
                opensOn="Hover"
                cssClass="e-tooltip-template-css e-tooltip-tab-feature"
                position="TopCenter"
              >
                <button
                  className="ai-assist-toolbar-button"
                  aria-label={button.text}
                  onClick={() => handleToolbarAction(button.key)}
                  type="button"
                >
                  <i className={getIconForButton(button)}></i>
                </button>
              </TooltipComponent>
            ))}
          </div>
          {showWritingActions && (
            <div
              className="ai-assist-writing-menu"
              role="menu"
              aria-label="Writing actions"
            >
              {[
                { key: 'chat' as const, label: 'Ask a question' },
                ...aiTextActions,
                { key: 'web_search' as const, label: 'Search web' },
              ].map((item) => (
                <button
                  key={item.key}
                  type="button"
                  role="menuitem"
                  onClick={() =>
                    item.key !== 'custom' && selectAction(item.key)
                  }
                >
                  {item.label}
                </button>
              ))}
            </div>
          )}
          {showEmojiPicker && (
            <div className="ai-assist-emoji-picker">
              <EmojiPicker onEmojiClick={handleEmojiClick} />
            </div>
          )}
          <div
            className="ai-assist-input-container"
            style={{ position: 'relative' }}
          >
            <input
              ref={inputRef}
              type="text"
              className="ai-assist-input"
              placeholder={
                editingMessageId
                  ? 'Edit your message...'
                  : action === 'web_search'
                    ? 'Search the public web'
                    : action === 'chat'
                      ? 'Ask anything'
                      : `${aiTextActions.find((item) => item.key === action)?.label}: enter text`
              }
              value={inputValue}
              onChange={(event) => setInputValue(event.target.value)}
              maxLength={3000}
              aria-label="Message"
              onKeyDown={handleKeyPress}
            />
            {showVariableDropdown && (
              <div
                className="ai-assist-variable-dropdown"
                style={{
                  position: 'absolute',
                  bottom: '100%',
                  left: 0,
                  right: 0,
                  marginBottom: '8px',
                  background: 'white',
                  border: '1px solid var(--blue-200)',
                  borderRadius: '8px',
                  boxShadow: '0 4px 12px rgba(0, 0, 0, 0.1)',
                  maxHeight: '200px',
                  overflowY: 'auto',
                  zIndex: 1000,
                }}
              >
                {VariableList.map((variable, index) => (
                  <div
                    key={index}
                    onMouseDown={(e) => {
                      e.preventDefault()
                      handleVariableSelect(variable)
                    }}
                    style={{
                      padding: '8px 12px',
                      cursor: 'pointer',
                      display: 'flex',
                      alignItems: 'center',
                      gap: '8px',
                      borderBottom: '1px solid var(--gray-100)',
                    }}
                    onMouseEnter={(e) => {
                      e.currentTarget.style.backgroundColor = 'var(--blue-50)'
                    }}
                    onMouseLeave={(e) => {
                      e.currentTarget.style.backgroundColor = 'transparent'
                    }}
                  >
                    <i
                      className="ti ti-variable"
                      style={{
                        fontSize: '16px',
                        color: 'var(--blue-500)',
                      }}
                    ></i>
                    <div style={{ flex: 1 }}>
                      <div
                        style={{
                          fontSize: '12px',
                          fontWeight: 500,
                          color: 'var(--blue-700)',
                        }}
                      >
                        {variable.text}
                      </div>
                      <div
                        style={{ fontSize: '10px', color: 'var(--gray-500)' }}
                      >
                        {variable.value}
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            )}
            <div className="ai-assist-input-icons">
              <button
                className={`ai-assist-input-icon-button ${isRecording ? 'recording' : ''}`}
                type="button"
                data-bs-toggle="tooltip"
                title={isRecording ? 'Stop dictation' : 'Dictate message'}
                onClick={handleMicrophoneClick}
                disabled={true}
                aria-label={isRecording ? 'Stop dictation' : 'Dictate message'}
              >
                <i
                  className={`mgc mgc_mic_fill ${isRecording ? 'recording-mic' : ''}`}
                ></i>
              </button>
              <button
                className="ai-assist-input-icon-button ai-assist-send-button"
                type="button"
                data-bs-toggle="tooltip"
                aria-label={
                  chat.isLoading
                    ? 'Cancel'
                    : isRecording
                      ? 'Stop recording'
                      : 'Send'
                }
                onClick={chat.isLoading ? chat.cancel : send}
                disabled={
                  !chat.isLoading &&
                  !isRecording &&
                  (!inputValue.trim() || !clinicId)
                }
              >
                {isRecording || chat.isLoading ? (
                  <i className="ti ti-square-filled"></i>
                ) : (
                  <i className="mgc_send_fill"></i>
                )}
              </button>
            </div>
          </div>
        </div>
      </div>
      {action === 'web_search' && (
        <p className="ai-assist-composer-status" role="status">
          Public web search. Do not include patient or confidential information.
        </p>
      )}
      {showHistoryDropdown &&
        chatSessions.length > 0 &&
        createPortal(
          <div
            className="ai-assist-history-dropdown"
            style={{
              top: historyDropdownPosition.top,
              left: historyDropdownPosition.left,
            }}
          >
            <div className="ai-assist-history-dropdown-header">
              Chat history
            </div>
            <div className="ai-assist-history-dropdown-list">
              {chatSessions.map((session) => (
                <div
                  key={session.id}
                  className={`ai-assist-history-item ${
                    currentSessionId === session.id ? 'active' : ''
                  }`}
                  onClick={() => handleSessionSelect(session.id)}
                >
                  <div className="ai-assist-history-item-content">
                    <div className="ai-assist-history-item-title">
                      {session.title}
                    </div>
                    <div className="ai-assist-history-item-time">
                      {formatTimestamp(session.timestamp)}
                    </div>
                  </div>
                  <button
                    className="ai-assist-history-item-delete"
                    onClick={(e) => handleDeleteSession(e, session.id)}
                    type="button"
                    aria-label="Delete chat"
                  >
                    <i className="mgc_delete_2_line"></i>
                  </button>
                </div>
              ))}
            </div>
          </div>,
          document.body,
        )}

      <InsertLinkModal
        isOpen={showInsertLinkModal}
        onRequestClose={() => setShowInsertLinkModal(false)}
        onInsertLink={handleInsertLink}
      />
    </div>
  )
}

const AIAssistSidePanel: React.FC<Props> = ({ isHidden, toggle }) => {
  const clinicId =
    useAppSelector((state) => state.clinic.selectedClinic?.clinic_id) ?? ''
  const tenantId = useAppSelector((state) => state.auth.tenantId)
  const authenticated = useAppSelector((state) => state.auth.isAuthenticated)
  let userId = ''
  try {
    userId = JSON.parse(localStorage.getItem('user') ?? '{}').user_id ?? ''
  } catch {
    /* Session is unavailable. */
  }
  if (isHidden || !authenticated || !tenantId || !userId) return null
  // Remount synchronously on identity/clinic changes: no old messages or late replies can cross contexts.
  return (
    <AssistantPanel
      key={JSON.stringify([tenantId, userId, clinicId])}
      clinicId={clinicId}
      toggle={toggle}
    />
  )
}

export default AIAssistSidePanel
